import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Phone, Mail, Calculator, CreditCard, Truck, MapPin, CalendarDays, CalendarPlus, Home, Lightbulb,
  Package, StickyNote, Search, MessageSquare, ArrowLeft, History, Pencil, Save, X, FileText, Undo2, BellRing,
  Building2, DoorOpen, Send,
} from "lucide-react";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/components/AuthGate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { InstructionBanner, Private, Money, AgeTimer, EmptyState, LoadingRows, ConfirmDeleteButton, FollowUpBadge, InvoiceBadge } from "@/components/Bits";
import QuotePdfModal from "@/components/QuotePdfModal";
import DepositModal from "@/components/DepositModal";
import SquareInvoiceModal from "@/components/SquareInvoiceModal";
import { AddNoteDialog } from "@/pages/Leads";
import { CommissionCard } from "@/components/CommissionCard";
import { LeadScopesCard } from "@/components/LeadScopesCard";
import { LF, f, LEAD_STATUSES, STATUS_PILL, nextStepHint, KNOWN_LEAD_FIELD_IDS, formatExtraValue, needsFollowUp, quietDays, HOME_SIZES, isLegacyLeadField, leadAccess } from "@/lib/fields";
import { fmtDate, gmailCompose, gmailSearch, calendarTemplate, smsLink, winBackSmsBody, payNudgeSmsBody } from "@/lib/format";
import { quoteSmsBody } from "@/lib/quote";
import { depositFromQuote } from "@/lib/pricing";
import { bookLeadAsJob } from "@/lib/leadActions";
import { listJobsApi, markBookedOverrideApi, apiErrorMessage } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import CustomerPageDialog from "@/components/portal/CustomerPageDialog";

const InfoRow = ({ icon: Icon, label, children, isPrivate = true, testId }) => (
  <div className="flex items-start gap-2 text-sm" data-testid={testId}>
    <Icon className="w-4 h-4 text-faint mt-0.5 shrink-0" />
    <span className="text-faint shrink-0">{label}:</span>
    {isPrivate ? <Private className="min-w-0 break-words font-medium text-primary">{children}</Private> : <span className="min-w-0 break-words font-medium text-primary">{children}</span>}
  </div>
);

export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { loadTable, loadSchema, records, tableState, updateRecord, createRecord, deleteRecord, schemas, invoicesForLead, loadSquareInvoices, rates } = useApp();
  const { role } = useAuth();
  const isSales = role === "sales";
  const isOwner = role === "owner";
  const [pdfOpen, setPdfOpen] = useState(false);
  const [depositOpen, setDepositOpen] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [booking, setBooking] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [jobForLead, setJobForLead] = useState(null);
  const [portalOpen, setPortalOpen] = useState(false);
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");
  const [overriding, setOverriding] = useState(false);

  useEffect(() => {
    loadTable("leads");
    loadSchema("leads");
    if (isOwner) {
      loadSquareInvoices();
      listJobsApi().then((js) => setJobForLead((js || []).find((j) => j.lead_id === id) || null)).catch(() => setJobForLead(null));
    }
  }, [loadTable, loadSchema, loadSquareInvoices, isOwner, id]);

  const lead = records("leads").find((r) => r.id === id);
  const { loading } = tableState("leads");

  if (!lead) {
    return (
      <div data-testid="lead-detail-page">
        <Button asChild variant="outline" size="sm" className="gap-1.5 mb-4" data-testid="lead-detail-back-btn">
          <Link to="/leads"><ArrowLeft className="w-4 h-4" /> Back to Leads</Link>
        </Button>
        {loading ? <LoadingRows /> : <EmptyState>This lead is gone or the link is wrong. Go back to Leads.</EmptyState>}
      </div>
    );
  }

  const name = f(lead, LF.name) || "No name";
  const phone = f(lead, LF.phone);
  const email = f(lead, LF.email);
  const quote = f(lead, LF.quote);
  const status = f(lead, LF.status) || "New";
  const depositPaid = !!f(lead, LF.depositPaid);
  const latestInvoice = isOwner ? invoicesForLead(lead.id)[0] : null;
  const unpaidInvoice = latestInvoice && ["UNPAID", "PARTIALLY_PAID", "SCHEDULED"].includes(latestInvoice.status) ? latestInvoice : null;

  const stampNote = (line) => {
    const old = f(lead, LF.notes) || "";
    updateRecord("leads", lead.id, { [LF.notes]: old ? `${old}\n${line}` : line }).catch(() => {});
  };

  const access = leadAccess(lead, schemas.leads);
  const extras = (schemas.leads || [])
    .filter((fd) => !KNOWN_LEAD_FIELD_IDS.has(fd.id) && !isLegacyLeadField(fd.name)
      && fd.id !== access.ids.pickup && fd.id !== access.ids.dropoff)
    .map((fd) => ({ ...fd, display: formatExtraValue(f(lead, fd.id)) }))
    .filter((x) => f(lead, x.id) !== undefined && f(lead, x.id) !== null && x.display !== "");

  const noteEntries = (f(lead, LF.notes) || "").split("\n").map((s) => s.trim()).filter(Boolean).reverse();

  const setStatus = (v) => updateRecord("leads", lead.id, { [LF.status]: v }).catch(() => {});

  const startEdit = () => {
    setForm({
      name: f(lead, LF.name) || "",
      phone: f(lead, LF.phone) || "",
      email: f(lead, LF.email) || "",
      moveDate: (f(lead, LF.moveDate) || "").slice(0, 10),
      homeSize: f(lead, LF.homeSize) || "",
      from: f(lead, LF.from) || "",
      to: f(lead, LF.to) || "",
    });
    setEditing(true);
  };

  const setF = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const saveEdit = async () => {
    if (!form.name.trim()) {
      toast.error("The lead needs a name.");
      return;
    }
    setSavingEdit(true);
    const fields = {
      [LF.name]: form.name.trim(),
      [LF.phone]: form.phone,
      [LF.email]: form.email,
      [LF.from]: form.from,
      [LF.to]: form.to,
    };
    if (form.moveDate) fields[LF.moveDate] = form.moveDate;
    if (form.homeSize) fields[LF.homeSize] = form.homeSize;
    try {
      await updateRecord("leads", lead.id, fields);
      toast.success("Lead updated.");
      setEditing(false);
    } catch {}
    setSavingEdit(false);
  };

  const bookAsJob = async () => {
    setBooking(true);
    try {
      const r = await bookLeadAsJob({ createRecord, updateRecord }, lead);
      toast.success(r?.booked
        ? "Booked — job is live on the Projects page."
        : "Job started (pending deposit). It books automatically once the deposit lands.");
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
    setBooking(false);
  };

  const markBookedOverride = async () => {
    if (overrideReason.trim().length < 5) {
      toast.error("Add a short reason (5+ characters).");
      return;
    }
    setOverriding(true);
    try {
      await markBookedOverrideApi(lead.id, overrideReason.trim());
      toast.success("Marked booked (owner override). Reason logged.");
      setStatus("Booked");
      setOverrideOpen(false);
      setOverrideReason("");
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
    setOverriding(false);
  };

  return (
    <div data-testid="lead-detail-page">
      <Button asChild variant="outline" size="sm" className="gap-1.5 mb-4" data-testid="lead-detail-back-btn">
        <Link to="/leads"><ArrowLeft className="w-4 h-4" /> Back to Leads</Link>
      </Button>

      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div className="min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <Private className="font-display text-[22px] sm:text-[26px] font-extrabold text-primary" data-testid="lead-detail-name">{name}</Private>
            {status === "New" && <AgeTimer createdTime={lead.createdTime} />}
            {needsFollowUp(lead) && <FollowUpBadge days={quietDays(lead)} />}
          </div>
          <div className="text-sm text-faint mt-1 space-x-3">
            {phone && <Private>{phone}</Private>}
            {email && <Private>{email}</Private>}
          </div>
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="lead-detail-status-select" className={`w-[140px] h-9 text-sm font-semibold border ${STATUS_PILL[status] || ""}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {LEAD_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {isOwner && jobForLead && (
        <Button data-testid="lead-customer-page-btn" variant="outline" size="sm" className="gap-1.5 mb-4" onClick={() => setPortalOpen(true)}>
          <Send className="w-4 h-4" /> Customer page (Job #{jobForLead.invoice_number})
        </Button>
      )}
      {portalOpen && jobForLead && (
        <CustomerPageDialog
          jobId={jobForLead.id}
          invoiceNumber={jobForLead.invoice_number}
          onClose={() => setPortalOpen(false)}
          onChanged={() => listJobsApi().then((js) => setJobForLead((js || []).find((j) => j.lead_id === id) || null)).catch(() => {})}
        />
      )}

      <InstructionBanner testId="lead-detail-hint">{nextStepHint(lead)}</InstructionBanner>

      <div className="grid lg:grid-cols-2 gap-4 items-start">
        <div className="space-y-4">
          <div className="surface p-5 space-y-3" data-testid="lead-detail-info">
            <div className="flex items-center justify-between">
              <h2 className="font-display font-bold text-primary">Move details</h2>
              {!editing && (
                <Button data-testid="lead-detail-edit-btn" variant="outline" size="sm" className="gap-1 text-xs" onClick={startEdit}>
                  <Pencil className="w-3.5 h-3.5" /> Edit
                </Button>
              )}
            </div>
            {editing ? (
              <div className="grid grid-cols-2 gap-3" data-testid="lead-detail-edit-form">
                <div className="col-span-2">
                  <Label>Name *</Label>
                  <Input data-testid="lead-edit-name-input" value={form.name} onChange={setF("name")} />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input data-testid="lead-edit-phone-input" value={form.phone} onChange={setF("phone")} />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input data-testid="lead-edit-email-input" type="email" value={form.email} onChange={setF("email")} />
                </div>
                <div>
                  <Label>Move date</Label>
                  <Input data-testid="lead-edit-date-input" type="date" value={form.moveDate} onChange={setF("moveDate")} />
                </div>
                <div>
                  <Label>Home size</Label>
                  <Select value={form.homeSize} onValueChange={(v) => setForm((s) => ({ ...s, homeSize: v }))}>
                    <SelectTrigger data-testid="lead-edit-size-select"><SelectValue placeholder="Pick size" /></SelectTrigger>
                    <SelectContent>{HOME_SIZES.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label>Moving from</Label>
                  <Input data-testid="lead-edit-from-input" value={form.from} onChange={setF("from")} />
                </div>
                <div className="col-span-2">
                  <Label>Moving to</Label>
                  <Input data-testid="lead-edit-to-input" value={form.to} onChange={setF("to")} />
                </div>
                <div className="col-span-2 flex gap-2">
                  <Button data-testid="lead-edit-save-btn" onClick={saveEdit} disabled={savingEdit} className="flex-1 gap-1.5 bg-accent hover:bg-accent-press">
                    <Save className="w-4 h-4" /> {savingEdit ? "Saving…" : "Save changes"}
                  </Button>
                  <Button data-testid="lead-edit-cancel-btn" variant="outline" onClick={() => setEditing(false)} className="gap-1.5">
                    <X className="w-4 h-4" /> Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <InfoRow icon={CalendarDays} label="Move date" isPrivate={false}>{fmtDate(f(lead, LF.moveDate))}</InfoRow>
                <InfoRow icon={Home} label="Home size" isPrivate={false}>{f(lead, LF.homeSize) || "Size TBD"}</InfoRow>
                <InfoRow icon={MapPin} label="Route">{f(lead, LF.from) || "?"} → {f(lead, LF.to) || "?"}</InfoRow>
                {access.pickup && (
                  <InfoRow icon={Building2} label="Pickup access" isPrivate={false} testId="lead-detail-pickup-access">{access.pickup}</InfoRow>
                )}
                {access.dropoff && (
                  <InfoRow icon={DoorOpen} label="Drop-off access" isPrivate={false} testId="lead-detail-dropoff-access">{access.dropoff}</InfoRow>
                )}
                {(f(lead, LF.specialty) || []).length > 0 && (
                  <InfoRow icon={Package} label="Specialty" isPrivate={false}>{(f(lead, LF.specialty) || []).join(", ")}</InfoRow>
                )}
                {f(lead, LF.contactMethod) && <InfoRow icon={Phone} label="Prefers" isPrivate={false}>{f(lead, LF.contactMethod)}</InfoRow>}
                {f(lead, LF.source) && <InfoRow icon={Lightbulb} label="Source" isPrivate={false}>{f(lead, LF.source)}</InfoRow>}
                {extras.map((x) => (
                  <InfoRow key={x.id} icon={Lightbulb} label={x.name}>{x.display}</InfoRow>
                ))}
              </>
            )}
          </div>

          <div className="surface p-5" data-testid="lead-detail-quote">
            <h2 className="font-display font-bold text-primary mb-2">Quote</h2>
            {quote ? (
              <div>
                <span className="font-bold text-primary text-lg">Quoted: <Money value={quote} /></span>
                {!isSales && (
                  <span className={`ml-2 text-sm font-semibold ${depositPaid ? "text-success" : "text-warning"}`}>
                    {depositPaid ? "Deposit paid" : "No deposit yet"}
                  </span>
                )}
                <div className="text-xs text-faint mt-1">Final price confirmed by phone.</div>
              </div>
            ) : (
              <span className="text-sm text-faint">No quote yet. Press Quote below to price this move.</span>
            )}
            {isOwner && invoicesForLead(lead.id).length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-3" data-testid="lead-detail-invoices">
                {invoicesForLead(lead.id).map((inv) => (
                  <InvoiceBadge key={inv.invoice_id} inv={inv} />
                ))}
              </div>
            )}
          </div>

          <CommissionCard lead={lead} isOwner={isOwner} />

          <LeadScopesCard leadId={lead.id} leadName={name} role={role} />

          <div className="surface p-5" data-testid="lead-detail-actions">
            <h2 className="font-display font-bold text-primary mb-3">Actions</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
              <Button data-testid="lead-detail-call-btn" asChild variant="outline" size="sm" className="gap-1 text-xs" disabled={!phone}>
                <a href={phone ? `tel:${phone}` : undefined}><Phone className="w-3.5 h-3.5" /> Call</a>
              </Button>
              <Button data-testid="lead-detail-email-btn" asChild variant="outline" size="sm" className="gap-1 text-xs">
                <a href={gmailCompose(email, "Your move with Haul Yeah Moving")} target="_blank" rel="noreferrer"><Mail className="w-3.5 h-3.5" /> Email</a>
              </Button>
              <Button data-testid="lead-detail-gmail-search-btn" asChild variant="outline" size="sm" className="gap-1 text-xs">
                <a href={gmailSearch(email || name)} target="_blank" rel="noreferrer"><Search className="w-3.5 h-3.5" /> Mail log</a>
              </Button>
              <Button data-testid="lead-detail-calendar-btn" asChild variant="outline" size="sm" className="gap-1 text-xs">
                <a
                  href={calendarTemplate(
                    `Move: ${name}`,
                    f(lead, LF.moveDate),
                    `Lead: ${name}\nEmail: ${email || "—"}\nPhone: ${phone || "—"}\nFrom: ${f(lead, LF.from) || "?"} → ${f(lead, LF.to) || "?"}`,
                    email
                  )}
                  target="_blank"
                  rel="noreferrer"
                >
                  <CalendarPlus className="w-3.5 h-3.5" /> Calendar
                </a>
              </Button>
              <Button data-testid="lead-detail-quote-btn" variant="outline" size="sm" className="gap-1 text-xs"
                onClick={() => navigate(`/scope-calculator?lead=${lead.id}&name=${encodeURIComponent(name || "")}`)}>
                <Calculator className="w-3.5 h-3.5" /> Quote
              </Button>
              <Button data-testid="lead-detail-text-quote-btn" asChild variant="outline" size="sm" className="gap-1 text-xs" disabled={!phone || !quote}>
                <a href={phone && quote ? smsLink(phone, quoteSmsBody(name, quote, depositFromQuote(quote, rates.depositPercent))) : undefined}>
                  <MessageSquare className="w-3.5 h-3.5" /> Text quote
                </a>
              </Button>
              {!!quote && ["Quoted", "Booked"].includes(status) && (
                <Button data-testid="lead-detail-pdf-btn" variant="outline" size="sm" className="gap-1 text-xs border-accent/40 text-accent-ink hover:bg-accent/10 hover:text-accent-ink" onClick={() => setPdfOpen(true)}>
                  <FileText className="w-3.5 h-3.5" /> Quote PDF
                </Button>
              )}
              <Button data-testid="lead-detail-note-btn" variant="outline" size="sm" className="gap-1 text-xs" onClick={() => setNoteOpen(true)}>
                <StickyNote className="w-3.5 h-3.5" /> Add note
              </Button>
              {["Lost", "Cold"].includes(status) && (
                <Button data-testid="lead-detail-winback-btn" asChild variant="outline" size="sm" className="gap-1 text-xs border-info/30 text-info hover:bg-info/10 hover:text-info" disabled={!phone}>
                  <a
                    href={phone ? smsLink(phone, winBackSmsBody(name)) : undefined}
                    onClick={() => stampNote(`Win-back text sent ${new Date().toLocaleDateString("en-US")}.`)}
                  >
                    <Undo2 className="w-3.5 h-3.5" /> Win back
                  </a>
                </Button>
              )}
              {unpaidInvoice && (
                <Button data-testid="lead-detail-nudge-btn" asChild variant="outline" size="sm" className="gap-1 text-xs border-warning/30 text-warning hover:bg-warning/10 hover:text-warning" disabled={!phone}>
                  <a
                    href={phone ? smsLink(phone, payNudgeSmsBody(name, unpaidInvoice.amount, unpaidInvoice.public_url)) : undefined}
                    onClick={() => stampNote(`Payment nudge text sent ${new Date().toLocaleDateString("en-US")}.`)}
                  >
                    <BellRing className="w-3.5 h-3.5" /> Nudge pay
                  </a>
                </Button>
              )}
              {!isSales && (
                <>
                  <Button data-testid="lead-detail-deposit-btn" variant="outline" size="sm" className="gap-1 text-xs" onClick={() => setDepositOpen(true)} disabled={!quote}>
                    <CreditCard className="w-3.5 h-3.5" /> Deposit
                  </Button>
                  <Button data-testid="lead-detail-square-invoice-btn" variant="outline" size="sm" className="gap-1 text-xs" onClick={() => setInvoiceOpen(true)}>
                    <FileText className="w-3.5 h-3.5" /> Invoice
                  </Button>
                  <Button
                    data-testid="lead-detail-book-btn"
                    size="sm"
                    className="gap-1 text-xs bg-primary hover:bg-[#26395f]"
                    onClick={bookAsJob}
                    disabled={booking || status === "Booked"}
                  >
                    <Truck className="w-3.5 h-3.5" /> {status === "Booked" ? "Booked" : booking ? "Booking…" : "Book as job"}
                  </Button>
                  {isOwner && status !== "Booked" && (
                    <Button
                      data-testid="lead-detail-mark-booked-override-btn"
                      variant="outline"
                      size="sm"
                      className="gap-1 text-xs border-warning/40 text-warning hover:bg-warning/10 hover:text-warning"
                      onClick={() => setOverrideOpen(true)}
                    >
                      <Truck className="w-3.5 h-3.5" /> Mark booked (override)
                    </Button>
                  )}
                </>
              )}
              {isOwner && (
                <ConfirmDeleteButton
                  what="this lead"
                  testId="lead-detail-delete-btn"
                  onConfirm={() =>
                    deleteRecord("leads", lead.id)
                      .then(() => { toast.success("Lead deleted."); navigate("/leads"); })
                      .catch(() => {})
                  }
                />
              )}
            </div>
          </div>
        </div>

        <div className="surface p-5" data-testid="lead-detail-notes">
          <h2 className="font-display font-bold text-primary mb-3 flex items-center gap-2">
            <History className="w-4 h-4 text-accent-ink" /> Notes history
          </h2>
          {noteEntries.length === 0 ? (
            <EmptyState>No notes yet. Press "Add note" after every call or text.</EmptyState>
          ) : (
            <div className="space-y-2">
              {noteEntries.map((line, i) => (
                <div key={i} data-testid="lead-note-entry" className="flex items-start gap-2 text-sm border border-border bg-surface-sunk/70 rounded-md px-3 py-2">
                  <StickyNote className="w-3.5 h-3.5 text-faint mt-0.5 shrink-0" />
                  <Private className="min-w-0 break-words whitespace-pre-wrap">{line}</Private>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {pdfOpen && <QuotePdfModal key={`pdf-${lead.id}`} lead={lead} open={pdfOpen} onOpenChange={setPdfOpen} />}
      {depositOpen && <DepositModal lead={lead} open={depositOpen} onOpenChange={setDepositOpen} />}
      {noteOpen && <AddNoteDialog lead={lead} open={noteOpen} onOpenChange={setNoteOpen} />}
      {invoiceOpen && <SquareInvoiceModal lead={lead} open={invoiceOpen} onOpenChange={setInvoiceOpen} />}
      <Dialog open={overrideOpen} onOpenChange={setOverrideOpen}>
        <DialogContent data-testid="mark-booked-override-dialog">
          <DialogHeader>
            <DialogTitle>Mark booked without a deposit</DialogTitle>
            <DialogDescription>
              Booking normally requires a received deposit. This owner override marks the job booked anyway and
              logs who did it, when, and why.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="override-reason">Reason (required)</Label>
            <Textarea
              id="override-reason"
              data-testid="mark-booked-override-reason"
              value={overrideReason}
              onChange={(e) => setOverrideReason(e.target.value)}
              placeholder="e.g. Repeat customer, paying cash on move day"
              rows={3}
            />
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setOverrideOpen(false)}>Cancel</Button>
            <Button
              data-testid="mark-booked-override-confirm"
              className="bg-primary hover:bg-[#26395f]"
              onClick={markBookedOverride}
              disabled={overriding || overrideReason.trim().length < 5}
            >
              {overriding ? "Saving…" : "Mark booked"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
